import { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import UserContext from "common/Context/UserContext";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import ParentNodeTreePicker from "common/Components/ParentNodeTreePicker/ParentNodeTreePicker";
import {
  buildParentSelectOptionsForRow,
  filterNodeTypeOptionsForTagEdit,
} from "pages/DataManagement/Tags/tagRowsMapper";
import {
  NODE_NAME_MAX_LENGTH,
  NODE_NAME_MESSAGES,
  nodeNameFormatError,
  validateNodeNameForSave,
} from "common/utils/nodeNameValidation";
import {
  ApplyButton,
  BulkFieldTextField,
  CancelButton,
  CheckboxCell,
  ErrorText,
  FieldRow,
  FieldsStack,
  InstructionText,
  PlaceholderAsValue,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  SubtitleText,
} from "./BulkEditTagsDialog.styles";

const optionShape = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

function createInitialBulkFieldState() {
  return {
    useNodeType: false,
    useSubtype: false,
    useParent: false,
    useName: false,
    nodeType: "",
    nodeSubType: "",
    parentNode: "",
    parentNodeId: null,
    nodeName: "",
  };
}

function commonNodeTypeFromSelectedRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const first = String(rows[0]?.nodeType ?? "");
  if (!rows.every((r) => String(r?.nodeType ?? "") === first)) return "";
  return first;
}

function getTagNodeTypeOption(nodeTypeOptions) {
  const fromReference = (nodeTypeOptions ?? []).find(
    (option) => String(option?.value ?? "").toLowerCase() === "tag"
  );
  return fromReference ?? { value: "tag", label: "Tag" };
}

export default function BulkEditTagsDialog({
  open,
  onClose,
  selectedRows,
  onApply,
  nodeTypeOptions,
  subtypeOptionsByNodeType = {},
  referencePayload = null,
  parentCandidates = [],
}) {
  const { getCustomerName } = useContext(UserContext);
  const customerName = getCustomerName?.() ?? "";
  const [bulkFields, setBulkFields] = useState(() => createInitialBulkFieldState());
  const [validationError, setValidationError] = useState("");

  const selectedRowCount = selectedRows?.length ?? 0;
  const isAllUncontextualizedSelection =
    selectedRowCount > 0 &&
    (selectedRows ?? []).every((r) => Boolean(r?.uncontextualized));
  const contextualizeNodeTypeOption = useMemo(
    () => getTagNodeTypeOption(nodeTypeOptions),
    [nodeTypeOptions]
  );

  const editableNodeTypeOptions = useMemo(
    () => filterNodeTypeOptionsForTagEdit(nodeTypeOptions),
    [nodeTypeOptions]
  );

  const hasNodeTypeValue = Boolean(bulkFields.nodeType);

  const bulkSubtypeSelectOptions = useMemo(
    () =>
      bulkFields.nodeType
        ? subtypeOptionsByNodeType[String(bulkFields.nodeType)] ?? []
        : [],
    [bulkFields.nodeType, subtypeOptionsByNodeType]
  );

  const bulkParentSelectOptions = useMemo(
    () =>
      bulkFields.nodeType
        ? buildParentSelectOptionsForRow(
            { nodeType: bulkFields.nodeType },
            referencePayload,
            parentCandidates,
            customerName
          )
        : [],
    [bulkFields.nodeType, referencePayload, parentCandidates, customerName]
  );

  useEffect(() => {
    if (!open) {
      setBulkFields(createInitialBulkFieldState());
      setValidationError("");
      return;
    }
    const nodeType = commonNodeTypeFromSelectedRows(selectedRows);
    setBulkFields({
      ...createInitialBulkFieldState(),
      nodeType: isAllUncontextualizedSelection
        ? String(contextualizeNodeTypeOption.value)
        : nodeType,
    });
  }, [
    contextualizeNodeTypeOption.value,
    isAllUncontextualizedSelection,
    open,
    selectedRows,
  ]);

  const mergeBulkFields = (patch) =>
    setBulkFields((previous) => ({ ...previous, ...patch }));

  const bulkNodeNameLiveError =
    nodeNameFormatError(bulkFields.nodeName) ||
    (bulkFields.nodeName.length >= NODE_NAME_MAX_LENGTH
      ? NODE_NAME_MESSAGES.MAX_LENGTH
      : "");

  const handleApply = () => {
    setValidationError("");
    if (isAllUncontextualizedSelection) {
      if (!bulkFields.useSubtype && !bulkFields.useParent && !bulkFields.useName) {
        setValidationError("Enable at least one field to contextualize.");
        return;
      }
      const patch = {};
      if (bulkFields.useSubtype) {
        if (!bulkSubtypeSelectOptions.length) {
          setValidationError(
            "No subtypes for this node type. Disable subtype or pick another type."
          );
          return;
        }
        if (!bulkFields.nodeSubType) {
          setValidationError("Select a node subtype or disable that field.");
          return;
        }
        patch.nodeSubType = bulkFields.nodeSubType;
      }
      if (bulkFields.useParent) {
        if (!bulkParentSelectOptions.length) {
          setValidationError(
            "No valid parents for this node type. Disable parent or pick another type."
          );
          return;
        }
        if (bulkFields.parentNodeId == null || bulkFields.parentNodeId === "") {
          setValidationError("Select a parent or disable that field.");
          return;
        }
        patch.parentNodeId = bulkFields.parentNodeId;
      }
      if (bulkFields.useName) {
        const nodeNameError = validateNodeNameForSave(bulkFields.nodeName);
        if (nodeNameError) {
          setValidationError(nodeNameError);
          return;
        }
        patch.nodeName = bulkFields.nodeName.trim();
      }
      onApply(patch);
      onClose();
      return;
    }

    if (
      !bulkFields.useNodeType &&
      !bulkFields.useSubtype &&
      !bulkFields.useParent &&
      !bulkFields.useName
    ) {
      setValidationError("Enable at least one field to update.");
      return;
    }
    const patch = {};
    if (bulkFields.useNodeType) {
      const normalizedNodeType = bulkFields.nodeType;
      if (!normalizedNodeType) {
        setValidationError("Select a node type or disable that field.");
        return;
      }
      patch.nodeType = normalizedNodeType;
    }
    if (bulkFields.useSubtype) {
      if (!bulkSubtypeSelectOptions.length) {
        setValidationError("No subtypes for this node type. Disable subtype or pick another type.");
        return;
      }
      if (!bulkFields.nodeSubType) {
        setValidationError("Select a node subtype or disable that field.");
        return;
      }
      patch.nodeSubType = bulkFields.nodeSubType;
    }
    if (bulkFields.useParent) {
      if (!bulkParentSelectOptions.length) {
        setValidationError(
          "No valid parents for this node type. Disable parent or pick another type."
        );
        return;
      }
      if (bulkFields.parentNodeId == null || bulkFields.parentNodeId === "") {
        setValidationError("Select a parent or disable that field.");
        return;
      }
      patch.parentNodeId = bulkFields.parentNodeId;
    }
    if (bulkFields.useName) {
      const nodeNameError = validateNodeNameForSave(bulkFields.nodeName);
      if (nodeNameError) {
        setValidationError(nodeNameError);
        return;
      }
      patch.nodeName = bulkFields.nodeName.trim();
    }

    onApply(patch);
    onClose();
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="bulk-edit-title"
        data-testid="bulk-edit-dialog"
      >
        <StyledDialogTitle id="bulk-edit-title">
          Bulk edit
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent dividers>
          <SubtitleText variant="subtitle1" component="div">
            {isAllUncontextualizedSelection
              ? `Contextualize ${selectedRowCount} selected row${selectedRowCount === 1 ? "" : "s"}.`
              : `Apply changes to ${selectedRowCount} selected row${selectedRowCount === 1 ? "" : "s"}.`}
          </SubtitleText>
          <InstructionText variant="body2" component="div">
            {isAllUncontextualizedSelection
              ? "Node type is Tag. Names are derived per metric unless you enable a shared node name (only when one name is valid for every selected metric). Subtype and parent are optional."
              : "Subtype and parent options follow the node type shown below. Only checked fields are applied."}
          </InstructionText>

          {validationError ? (
            <ErrorText component="div">{validationError}</ErrorText>
          ) : null}

          <FieldsStack>
            <FieldRow>
              <CheckboxCell>
                <Checkbox
                  checked={bulkFields.useNodeType}
                  onChange={(e) =>
                    mergeBulkFields({ useNodeType: e.target.checked })
                  }
                  disabled={isAllUncontextualizedSelection}
                  inputProps={{ "aria-label": "Enable node type" }}
                />
              </CheckboxCell>
              <BulkFieldTextField
                select
                size="small"
                disabled={!bulkFields.useNodeType || isAllUncontextualizedSelection}
                value={bulkFields.nodeType}
                onChange={(e) =>
                  mergeBulkFields({
                    nodeType: e.target.value,
                    nodeSubType: "",
                    parentNode: "",
                    parentNodeId: null,
                  })
                }
                InputProps={{
                  readOnly: isAllUncontextualizedSelection,
                }}
                data-testid="bulk-node-type"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === "") {
                      return <PlaceholderAsValue>Node Type</PlaceholderAsValue>;
                    }
                    const opt = editableNodeTypeOptions.find(
                      (o) => String(o.value) === String(selected)
                    );
                    return opt?.label ?? selected;
                  },
                }}
              >
                {(isAllUncontextualizedSelection
                  ? [contextualizeNodeTypeOption]
                  : editableNodeTypeOptions
                ).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </BulkFieldTextField>
            </FieldRow>

            <FieldRow>
              <CheckboxCell>
                <Checkbox
                  checked={bulkFields.useSubtype}
                  onChange={(e) => mergeBulkFields({ useSubtype: e.target.checked })}
                  inputProps={{ "aria-label": "Enable node subtype" }}
                />
              </CheckboxCell>
              <BulkFieldTextField
                select
                size="small"
                disabled={!bulkFields.useSubtype}
                value={bulkFields.nodeSubType}
                onChange={(e) => mergeBulkFields({ nodeSubType: e.target.value })}
                data-testid="bulk-node-subtype"
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (selected) => {
                    if (selected === "") {
                      return <PlaceholderAsValue>Node Subtype</PlaceholderAsValue>;
                    }
                    const opt = bulkSubtypeSelectOptions.find(
                      (o) => String(o.value) === String(selected)
                    );
                    return opt?.label ?? selected;
                  },
                }}
              >
                {!hasNodeTypeValue ? (
                  <MenuItem value="" disabled>
                    <em>Select a node type</em>
                  </MenuItem>
                ) : !bulkSubtypeSelectOptions.length ? (
                  <MenuItem value="" disabled>
                    <em>No subtypes for this type</em>
                  </MenuItem>
                ) : (
                  bulkSubtypeSelectOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))
                )}
              </BulkFieldTextField>
            </FieldRow>

            <FieldRow>
              <CheckboxCell>
                <Checkbox
                  checked={bulkFields.useParent}
                  onChange={(e) => mergeBulkFields({ useParent: e.target.checked })}
                  inputProps={{ "aria-label": "Enable parent node" }}
                />
              </CheckboxCell>
              <ParentNodeTreePicker
                nodeType={bulkFields.nodeType}
                referencePayload={referencePayload}
                parentCandidates={parentCandidates}
                disabled={!bulkFields.useParent || !hasNodeTypeValue}
                valueId={bulkFields.parentNodeId}
                onChange={(patch) => mergeBulkFields(patch)}
                inputTestId="bulk-open-select-parent"
                placeholder="Parent node"
              />
            </FieldRow>

            <FieldRow>
              <CheckboxCell>
                <Checkbox
                  checked={bulkFields.useName}
                  onChange={(e) => mergeBulkFields({ useName: e.target.checked })}
                  inputProps={{ "aria-label": "Enable node name" }}
                />
              </CheckboxCell>
              <BulkFieldTextField
                size="small"
                disabled={!bulkFields.useName}
                value={bulkFields.nodeName}
                onChange={(e) => {
                  mergeBulkFields({ nodeName: e.target.value });
                  if (
                    validationError === NODE_NAME_MESSAGES.MAX_LENGTH ||
                    validationError === NODE_NAME_MESSAGES.NO_SPECIAL_CHARS ||
                    validationError === NODE_NAME_MESSAGES.REQUIRED
                  ) {
                    setValidationError("");
                  }
                }}
                placeholder="Node Name"
                error={bulkFields.useName && Boolean(bulkNodeNameLiveError)}
                helperText={
                  bulkFields.useName && bulkNodeNameLiveError
                    ? bulkNodeNameLiveError
                    : undefined
                }
                inputProps={{
                  "data-testid": "bulk-node-name",
                  maxLength: NODE_NAME_MAX_LENGTH,
                }}
              />
            </FieldRow>
          </FieldsStack>
        </StyledDialogContent>
        <StyledDialogActions>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <ApplyButton variant="contained" onClick={handleApply} data-testid="bulk-edit-apply">
            {isAllUncontextualizedSelection
              ? selectedRowCount === 1
                ? "Contextualize Node"
                : `Contextualize (${selectedRowCount})`
              : "Apply"}
          </ApplyButton>
        </StyledDialogActions>
      </StyledDialog>

    </>
  );
}

const subtypeMapShape = PropTypes.objectOf(
  PropTypes.arrayOf(optionShape)
);

BulkEditTagsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired,
  nodeTypeOptions: PropTypes.arrayOf(optionShape).isRequired,
  subtypeOptionsByNodeType: subtypeMapShape,
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
};

BulkEditTagsDialog.defaultProps = {
  selectedRows: [],
  subtypeOptionsByNodeType: {},
  referencePayload: null,
  parentCandidates: [],
};
